import { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { scopeToSite } from '../../lib/siteScope';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import PageHeader from '../../components/ui/PageHeader';
import EmptyState from '../../components/ui/EmptyState';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import { toast } from '../../components/ui/Toast';
import {
  IconFileText,
  IconMail,
  IconDownload,
  IconTractor,
  IconCheck,
} from '@tabler/icons-react';

export default function InsurancePack() {
  const { orgId, org, userProfile, role } = useAuth();
  const siteId = userProfile?.siteId;
  const [machines, setMachines] = useState([]);
  const [selected, setSelected] = useState([]);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!orgId) return;
      try {
        const snap = await getDocs(collection(db, 'organisations', orgId, 'machines'));
        setMachines(scopeToSite(snap.docs.map(d => ({ id: d.id, ...d.data() })), role, siteId));
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    load();
  }, [orgId]);

  const toggleMachine = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectAll = () => setSelected(machines.map(m => m.id));
  const clearAll = () => setSelected([]);

  const handleGenerate = async () => {
    if (!selected.length) { toast.error('Select at least one machine'); return; }
    setGenerating(true);
    await new Promise(r => setTimeout(r, 1500));
    toast.success('Insurance pack generated — PDF export coming soon');
    setGenerating(false);
  };

  const handleEmail = async () => {
    if (!selected.length) { toast.error('Select at least one machine'); return; }
    if (!email.trim() || !email.includes('@')) { toast.error('Please enter a valid email address'); return; }
    setGenerating(true);
    await new Promise(r => setTimeout(r, 1500));
    toast.success('Insurance pack sent to ' + email);
    setGenerating(false);
  };

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', maxWidth: 720, margin: '0 auto' }}>
        <PageHeader
          title="Insurance pack"
          subtitle="Generate machine profiles for insurance claims"
        />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 400 }}>Select machines</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={selectAll} style={{ fontSize: 12, color: 'var(--color-brand)', background: 'none', border: 'none', cursor: 'pointer' }}>All</button>
                <button onClick={clearAll} style={{ fontSize: 12, color: 'var(--color-text-tertiary)', background: 'none', border: 'none', cursor: 'pointer' }}>Clear</button>
              </div>
            </div>
            {loading ? <LoadingSkeleton rows={4} card /> : machines.length === 0 ? (
              <Card><EmptyState icon={<IconTractor size={28} stroke={1.5} />} title="No machines" message="Add machines to generate insurance packs" /></Card>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {machines.map(machine => {
                  const isSelected = selected.includes(machine.id);
                  return (
                    <div
                      key={machine.id}
                      onClick={() => toggleMachine(machine.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 14px', borderRadius: 'var(--radius-md)',
                        border: '0.5px solid',
                        borderColor: isSelected ? 'var(--color-brand)' : 'var(--color-border-light)',
                        background: isSelected ? 'var(--color-brand-light)' : 'var(--color-bg-primary)',
                        cursor: 'pointer', transition: 'all 0.15s',
                      }}
                    >
                      <div style={{ width: 20, height: 20, borderRadius: 4, border: '1.5px solid ' + (isSelected ? 'var(--color-brand)' : 'var(--color-border-medium)'), background: isSelected ? 'var(--color-brand)' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {isSelected && <IconCheck size={12} color="#fff" stroke={2.5} />}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, color: isSelected ? 'var(--color-brand)' : 'var(--color-text-primary)' }}>
                          {machine.makeLabel} {machine.modelLabel}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 1 }}>
                          {machine.registration || machine.serialNumber || '—'} · {machine.siteName}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 12 }}>Generate pack</div>
            <Card style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 12, lineHeight: 1.6 }}>
                {selected.length === 0 ? 'No machines selected' : selected.length + ' machine' + (selected.length !== 1 ? 's' : '') + ' selected'}
              </div>
              <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 16, lineHeight: 1.6 }}>
                The insurance pack includes full machine details, service history, current hours, registration, serial number and all documents.
              </div>
              <Button
                variant="primary"
                fullWidth
                icon={<IconDownload size={14} stroke={1.5} />}
                onClick={handleGenerate}
                disabled={generating || !selected.length}
                loading={generating}
                style={{ marginBottom: 8 }}
              >
                Generate PDF
              </Button>
            </Card>

            <Card>
              <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 12 }}>Email directly</div>
              <div style={{ marginBottom: 10 }}>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                  Recipient email
                </label>
                <input
                  type="email"
                  placeholder="claims@insurer.co.uk"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box' }}
                />
              </div>
              <Button
                variant="secondary"
                fullWidth
                icon={<IconMail size={14} stroke={1.5} />}
                onClick={handleEmail}
                disabled={generating || !selected.length || !email.trim()}
                loading={generating}
              >
                Send by email
              </Button>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}